import { useState, useEffect } from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { MxTabBar } from "@/components/shared/MxTabBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { usePurchaseOrders } from "@/hooks/usePurchaseOrders";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { CreatePODialog } from "@/components/purchase-orders/CreatePODialog";
import { PODetailSheet } from "@/components/purchase-orders/PODetailSheet";
import type { PurchaseOrder, POStatus } from "@/hooks/usePurchaseOrders";

const PAGE_SIZE = 12;

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800",
  submitted: "bg-blue-100 text-blue-800",
  sent: "bg-indigo-100 text-indigo-800",
  partial: "bg-yellow-100 text-yellow-800",
  complete: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function PurchaseOrders() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [page, setPage] = useState(0);

  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
    if (debounceTimer) clearTimeout(debounceTimer);
    setDebounceTimer(setTimeout(() => setDebouncedSearch(value), 300));
  };

  const { data: pos = [], isLoading } = usePurchaseOrders({
    search: debouncedSearch || undefined,
    status: statusFilter !== "all" ? statusFilter as POStatus : undefined,
  });
  const { data: vendors = [] } = useVendorContacts();

  // Keep selectedPO in sync with fresh query data so the detail sheet
  // (and the PDF it generates) always reflects the latest PO totals.
  useEffect(() => {
    if (selectedPO) {
      const fresh = pos.find((p) => p.id === selectedPO.id);
      if (fresh) setSelectedPO(fresh);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos]);

  const vendorMap = vendors.reduce<Record<string, string>>((acc, v) => {
    acc[v.id] = v.company || "Unknown";
    return acc;
  }, {});

  const totalPages = Math.ceil(pos.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const paginatedPOs = pos.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className="flex flex-col min-h-screen">
      <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Purchase Orders</h1>
        </div>
        <CreatePODialog />
      </header>
      <MxTabBar />

      <div className="flex-1 p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search PO number..." className="pl-8" value={searchTerm} onChange={(e) => handleSearchChange(e.target.value)} />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : pos.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground mb-4">
              {debouncedSearch || statusFilter !== "all" ? "No purchase orders match your filters" : "No purchase orders yet"}
            </p>
            {!debouncedSearch && statusFilter === "all" && <CreatePODialog />}
          </Card>
        ) : (
          <div className="border rounded-lg bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPOs.map((po) => (
                  <TableRow
                    key={po.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => { setSelectedPO(po); setSheetOpen(true); }}
                  >
                    <TableCell className="font-mono font-medium">{po.po_number}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {po.vendor_id ? vendorMap[po.vendor_id] || "—" : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${STATUS_COLORS[po.status] || ""}`}>{po.status}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{po.order_date || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{po.expected_date || "—"}</TableCell>
                    <TableCell className="text-right font-medium">${po.total.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {pos.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, pos.length)} of {pos.length} purchase order{pos.length !== 1 ? "s" : ""}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-7 w-7" disabled={safePage === 0} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs text-muted-foreground px-2">
                  Page {safePage + 1} of {totalPages}
                </span>
                <Button variant="outline" size="icon" className="h-7 w-7" disabled={safePage >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <PODetailSheet po={selectedPO} open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
}
